import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { localized } from "@/theme/tenantTheme";
import { resolveImage } from "@/lib/media";
import { Skeleton } from "@/components/ui/skeleton";

export function CityAbout({ about, landmarks, adSlot, loading = false }) {
  const { t, lang, tenant } = useApp();
  const cityName = localized(lang, tenant, "name");

  return (
    <section className="relative mx-auto w-full max-w-7xl px-5 py-14 sm:px-8" data-testid="city-about-section">
      {adSlot && <div className="mb-6 lg:absolute lg:right-5 lg:top-14 lg:mb-0 lg:w-64">{adSlot}</div>}
      <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
        <div className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
            {t("cityPage.aboutTitle", { city: cityName })}
          </h2>
          {/* Line breaks the city desk typed are kept: one <p> per non-empty line. */}
          <div className="max-w-xl space-y-3 text-base leading-relaxed text-muted-foreground" data-testid="city-about-text">
            {(about ? about.split(/\r?\n+/).map((x) => x.trim()).filter(Boolean) : [t("cityPage.aboutEmpty")]).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <h3 className="font-heading text-xl font-semibold tracking-tight">
              {t("cityPage.exploreTitle", { city: cityName })}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("cityPage.exploreSubtitle")}</p>
          </div>
          {loading && landmarks.length === 0 ? (
            // RF-21: loading is not empty - no "no landmarks yet" before the fetch answers.
            <Skeleton className="h-20 w-full rounded-2xl" data-testid="landmarks-loading" />
          ) : landmarks.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("cityPage.landmarksEmpty")}</p>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2" data-testid="landmark-grid">
              {landmarks.map((l) => (
                <Link
                  key={l.id}
                  to={`/place/${l.slug}`}
                  data-testid={`landmark-card-${l.slug}`}
                  className="group overflow-hidden rounded-2xl border border-border bg-card transition-transform duration-200 hover:-translate-y-1"
                >
                  <div className="h-40 overflow-hidden bg-muted">
                    {l.image_url ? (
                      <img
                        src={resolveImage(l.image_url)}
                        alt={localized(lang, l, "name")}
                        width={400}
                        height={160}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div
                        className="flex h-full w-full items-center justify-center text-4xl font-semibold text-muted-foreground/40"
                        data-testid={`landmark-placeholder-${l.slug}`}
                        aria-hidden="true"
                      >
                        {localized(lang, l, "name").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5 p-5">
                    <p className="font-heading text-base font-semibold">{localized(lang, l, "name")}</p>
                    <p className="line-clamp-2 text-sm text-muted-foreground">{localized(lang, l, "summary")}</p>
                    <span className="inline-flex items-center gap-1 pt-1 text-sm font-medium text-[hsl(var(--primary))]">
                      {t("cityPage.explore")} <ArrowUpRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
